import { useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrEinstellung } from './typen';
import { HERKUNFT } from './gemeinsam';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection,
  type Spalte,
} from '../../muster';

/**
 * Die Einstellungen — die sechs Festlegungen und weitere Parameter.
 *
 * Umzug U6c: Die Bearbeitung lag bisher als aufklappende Zeile in der
 * Tabelle (`<td colSpan={6}>`) — dabei verschwand die Zeile, die man gerade
 * ändert, aus der Liste. Jetzt Tabelle (`DataTable`) und Bearbeitung
 * (`Drawer` mit `FormSection`/`Feld`) getrennt; die Zeile bleibt sichtbar
 * und hervorgehoben.
 */
export function Einstellungen({ api, projektId, einstellungen, onGeaendert }: {
  api: ApiClient; projektId: string; einstellungen: MsrEinstellung[]; onGeaendert: () => void;
}) {
  const [offen, setOffen] = useState<MsrEinstellung | null>(null);
  const [wert, setWert] = useState('');
  const [zahlwert, setZahlwert] = useState<number | null>(null);
  const [begruendung, setBegruendung] = useState('');
  const [speichertLaeuft, setSpeichertLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bearbeiten = (e: MsrEinstellung) => {
    setOffen(e);
    setWert(e.wert);
    setZahlwert(e.zahlwert ?? null);
    setBegruendung(e.begruendung ?? '');
    setFehler(null);
  };

  const speichern = async () => {
    if (!offen) return;
    setSpeichertLaeuft(true);
    setFehler(null);
    try {
      await api.msrEinstellungSetzen(projektId, offen.schluessel, {
        wert, zahlwert: zahlwert ?? undefined, begruendung,
      });
      setOffen(null);
      onGeaendert();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setSpeichertLaeuft(false);
    }
  };

  const spalten: Spalte<MsrEinstellung>[] = [
    {
      schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
      wert: (e) => e.schluessel,
      inhalt: (e) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.schluessel}</span>
      ),
    },
    { schluessel: 'wert', titel: 'Wert', wert: (e) => e.zahlwert ?? e.wert },
    { schluessel: 'einheit', titel: 'Einheit', wert: (e) => e.einheit ?? null },
    { schluessel: 'begruendung', titel: 'Begründung', wert: (e) => e.begruendung ?? null },
    { schluessel: 'herkunft', titel: 'Herkunft', wert: (e) => HERKUNFT[e.herkunft] },
    {
      schluessel: 'aktion', titel: 'Ändern', breite: '120px', sortierbar: false,
      wert: () => '',
      inhalt: (e) => (
        <button type="button" className="m-knopf" onClick={() => bearbeiten(e)}>
          Bearbeiten
        </button>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Einstellungen"
      beschreibung="Jede Änderung wirkt erst beim nächsten Ableitungslauf."
    >
      <DataTable
        name="Einstellungen"
        spalten={spalten}
        zeilen={einstellungen}
        schluessel={(e) => e.schluessel}
        dicht
        ausfuhr
        ausfuhrDateiname="msr-einstellungen"
        aktiveZeile={offen?.schluessel}
        kontextmenue={[
          { beschriftung: 'Bearbeiten …', beiWahl: bearbeiten },
        ]}
        leer={(
          <EmptyState
            titel="Für dieses Projekt ist keine Einstellung hinterlegt."
            erklaerung={'Einstellungen entstehen mit dem ersten Ableitungslauf; bis dahin '
              + 'gelten die Vorlagenwerte der Spezifikation.'}
          />
        )}
      />

      <Drawer
        offen={offen !== null}
        titel={offen ? `Einstellung ${offen.schluessel}` : ''}
        beschreibung={offen
          ? `Herkunft ${HERKUNFT[offen.herkunft]}`
            + (offen.einheit ? ` · Einheit ${offen.einheit}` : '')
          : undefined}
        beiSchliessen={() => setOffen(null)}
        fuss={(
          <>
            <button type="button" className="m-knopf m-knopf-haupt"
              disabled={speichertLaeuft || begruendung.trim() === ''}
              onClick={() => void speichern()}>
              {speichertLaeuft ? 'speichert …' : 'Speichern'}
            </button>
            <button type="button" className="m-knopf" onClick={() => setOffen(null)}>
              Abbrechen
            </button>
          </>
        )}
      >
        <FormSection titel="Wert" spalten={2}>
          <Feld beschriftung="Wert (Text)" wert={wert}
            beiAenderung={(w) => setWert(w as string)} />
          <Feld art="zahl" beschriftung="Zahlwert" einheit={offen?.einheit ?? undefined}
            wert={zahlwert} beiAenderung={(w) => setZahlwert(w as number | null)} />
          <Feld beschriftung="Begründung" wert={begruendung} pflicht breite="voll"
            hinweis="Ohne Begründung ist eine Festlegung später nicht nachvollziehbar."
            fehler={begruendung.trim() === '' ? 'Eine Begründung ist Pflicht.' : undefined}
            beiAenderung={(w) => setBegruendung(w as string)} />
        </FormSection>

        {fehler && (
          <ErrorState
            klein
            meldung="Die Einstellung konnte nicht gespeichert werden."
            technisch={fehler}
            beiErneutVersuchen={() => void speichern()}
          />
        )}
      </Drawer>
    </ContentSection>
  );
}
